import {
  EcommerceSocketEvents,
  type SuggestionProgressEventPayload,
} from './ecommerce.socket.interface';
import { BaseSocket } from '@/sockets/base.socket';
import { queryClient } from '@/plugins/vue-query';
import { useEcommerceStore } from '@/modules/flokee/stores/ecommerce.store';

/**
 * Handles WebSocket connections for ecommerce-related real-time communications.
 */
export class EcommerceSocket extends BaseSocket {
  private static readonly CHANNEL_PREFIX = 'private-flok.ecommerces';

  /**
   * Creates an instance of EcommerceSocket.
   * @param {number} ecommerce_id - The ID of the ecommerce to subscribe to.
   */
  constructor(ecommerce_id: number) {
    const channelName = `${EcommerceSocket.CHANNEL_PREFIX}.${ecommerce_id}`;
    super(channelName);
  }

  protected setupEventListeners(): void {
    this.onScoreUpdated(() => {
      queryClient.invalidateQueries({
        queryKey: ['ecommerce-score'],
      });
    });

    this.onApprovalUpdated(async () => {
      const ecommerceStore = useEcommerceStore();
      await queryClient.removeQueries({ queryKey: ['plan-subscription-approvals'] });
      ecommerceStore.getPlanSubscriptionApprovals();
    });
  }

  /**
   * Binds a callback to handle suggestion progress events.
   * @param {Function} callback - The callback to execute when suggestion progress is received.
   */
  onSuggestionProgress(callback: (data: SuggestionProgressEventPayload) => void): void {
    this.bind(EcommerceSocketEvents.SuggestionProgress, callback);
  }

  /**
   * Binds a callback to handle ecommerce score updated events.
   * @param {Function} callback - The callback to execute when ecommerce score is updated.
   */
  onScoreUpdated(callback: () => void): void {
    this.bind(EcommerceSocketEvents.ScoreUpdated, callback);
  }

  /**
   * Binds a callback to handle ecommerce approval updated events.
   * @param {Function} callback - The callback to execute when ecommerce approval is updated.
   */
  onApprovalUpdated(callback: () => void): void {
    this.bind(EcommerceSocketEvents.ApprovalUpdated, callback);
  }
}
